import Swal from "sweetalert2";
import {
  request_assign_member_access,
  request_delete_organization_member,
  request_unassign_member_access,
} from "./organization_members_api";
import {
  get_initials,
  get_member_permission_ids_for_feature,
  get_member_profile_picture,
} from "./organization_members_helpers";

export default {
  remove_organization_member(member) {
    if (!this.get_company_id()) {
      this.show_error("Organisation id is missing. Please refresh and try again.");
      return;
    }

    Swal.fire({
      title: "Remove member?",
      text: `${member.first_name} ${member.last_name} will lose organisation access and their user account will be deactivated.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
    }).then((result) => {
      if (!result.isConfirmed) return;
      request_delete_organization_member({
          organization_id: this.get_company_id(),
          member_id: member.id,
        })
        .then((response) => {
          if (response?.status === "success") {
            this.remove_member_from_datatable(member.id);
            this.organization_members = this.organization_members.filter((item) => Number(item.id) !== Number(member.id));
            this.show_success("Member removed and deactivated");
            return;
          }
          this.show_error(response?.message || "Could not remove organisation member.");
        })
        .catch((xhr) => this.show_error(xhr?.responseJSON?.message || "Could not remove organisation member."));
    });
  },
  open_manage_member(member) {
    this.selected_member = member;
    this.member_access_errors = {};
    const features = this.organization_features.filter((feature) => (
      this.get_member_permission_ids(member, feature.id).some((permission_id) => (
        this.get_assignable_permission_ids(feature).has(Number(permission_id))
      ))
    ));
    const feature_permissions = this.organization_features.map((feature) => ({
      feature_id: Number(feature.id),
      permission_ids: this.get_member_permission_ids(member, feature.id)
        .map(Number)
        .filter((permission_id) => this.get_assignable_permission_ids(feature).has(permission_id)),
    }));
    const expanded_feature_ids = features.map((feature) => Number(feature.id));
    this.member_access = {
      features,
      permission_ids: [],
      feature_permissions,
      expanded_feature_ids,
    };
    $("#manage_organization_member_modal").modal("show");
  },
  member_feature_is_enabled(feature) {
    return this.member_access.features.some((item) => Number(item.id) === Number(feature.id));
  },
  get_assignable_permissions(feature) {
    return Array.isArray(feature?.permissions)
      ? feature.permissions
      : this.organization_permissions;
  },
  get_assignable_permission_ids(feature) {
    return new Set(
      this.get_assignable_permissions(feature).map((permission) => Number(permission.id))
    );
  },
  set_member_feature_enabled(feature, is_enabled) {
    if (!is_enabled) {
      this.member_access.features = this.member_access.features.filter((item) => Number(item.id) !== Number(feature.id));
      this.set_feature_permission_ids(feature, []);
      return;
    }
    if (!this.member_feature_is_enabled(feature)) {
      this.member_access.features.push(feature);
    }
    this.set_feature_permission_ids(
      feature,
      this.get_assignable_permissions(feature).map((permission) => Number(permission.id))
    );
    this.expand_member_feature(feature);
  },
  get_feature_permission_entry(feature) {
    return this.member_access.feature_permissions.find((entry) => Number(entry.feature_id) === Number(feature.id));
  },
  get_feature_permission_ids(feature) {
    return this.get_feature_permission_entry(feature)?.permission_ids || [];
  },
  set_feature_permission_ids(feature, permission_ids) {
    const entry = this.get_feature_permission_entry(feature);
    if (entry) {
      entry.permission_ids = permission_ids;
      return;
    }
    this.member_access.feature_permissions.push({ feature_id: Number(feature.id), permission_ids });
  },
  member_permission_is_enabled(feature, permission) {
    return this.get_feature_permission_ids(feature).map(Number).includes(Number(permission.id));
  },
  set_member_permission_enabled(feature, permission, is_enabled) {
    const permission_id = Number(permission.id);
    const current_ids = this.get_feature_permission_ids(feature).map(Number);
    const permission_ids = is_enabled
      ? [...new Set([...current_ids, permission_id])]
      : current_ids.filter((id) => id !== permission_id);
    this.set_feature_permission_ids(feature, permission_ids);
  },
  member_feature_is_expanded(feature) {
    return this.member_access.expanded_feature_ids.includes(Number(feature.id));
  },
  expand_member_feature(feature) {
    const feature_id = Number(feature.id);
    if (!this.member_access.expanded_feature_ids.includes(feature_id)) {
      this.member_access.expanded_feature_ids.push(feature_id);
    }
  },
  toggle_member_feature_expanded(feature) {
    const feature_id = Number(feature.id);
    this.member_access.expanded_feature_ids = this.member_feature_is_expanded(feature)
      ? this.member_access.expanded_feature_ids.filter((id) => id !== feature_id)
      : [...this.member_access.expanded_feature_ids, feature_id];
  },
  get_feature_access_requests(feature) {
    const assignable_permission_ids = this.get_assignable_permission_ids(feature);
    const current_ids = this.get_member_permission_ids(this.selected_member, feature.id)
      .map(Number)
      .filter((permission_id) => assignable_permission_ids.has(permission_id));
    const next_ids = this.member_feature_is_enabled(feature)
      ? this.get_feature_permission_ids(feature).map(Number)
      : [];
    const payload = {
      organization_id: this.get_company_id(),
      feature_id: feature.id,
      member_ids: [this.selected_member.id],
    };
    const added_ids = next_ids.filter((id) => !current_ids.includes(id));
    const removed_ids = current_ids.filter((id) => !next_ids.includes(id));
    return this.create_feature_permission_requests(payload, added_ids, removed_ids);
  },
  create_feature_permission_requests(payload, added_ids, removed_ids) {
    const requests = [];
    if (added_ids.length) {
      requests.push(request_assign_member_access({ ...payload, permission_ids: added_ids }));
    }
    if (removed_ids.length) {
      requests.push(request_unassign_member_access({ ...payload, permission_ids: removed_ids }));
    }
    return requests;
  },
  complete_member_access_save(responses) {
    if (responses.some((response) => response?.status !== "success")) {
      this.member_access_errors.form = "Could not update member access.";
      return;
    }
    this.show_success("Member access updated");
    $("#manage_organization_member_modal").modal("hide");
    this.fetch_organization_members();
  },
  save_member_access() {
    if (!this.selected_member) return;
    this.is_saving_access = true;
    this.member_access_errors = {};

    const requests = this.organization_features.flatMap((feature) => (
      this.get_feature_access_requests(feature)
    ));

    if (!requests.length) {
      this.show_success("No changes to save");
      this.is_saving_access = false;
      return;
    }

    Promise.all(requests)
      .then((responses) => this.complete_member_access_save(responses))
      .catch((xhr) => {
        this.member_access_errors.form = xhr?.responseJSON?.message || "Could not update member access.";
      })
      .finally(() => {
        this.is_saving_access = false;
      });
  },
  get_member_permission_ids(member, feature_id = null) {
    return get_member_permission_ids_for_feature(member, feature_id);
  },
  get_initials(member) {
    return get_initials(member);
  },
  get_member_profile_picture(member) {
    return get_member_profile_picture(member);
  },
  set_invite_error(message) {
    if (String(message).toLowerCase().includes("email")) {
      this.invite_errors.email = message;
      return;
    }
    this.invite_errors.form = message;
  },
  set_new_user_error(message) {
    if (String(message).toLowerCase().includes("email")) {
      this.new_user_errors.email = message;
      return;
    }
    if (String(message).toLowerCase().includes("phone")) {
      this.new_user_errors.phone_number = message;
      return;
    }
    this.new_user_errors.form = message;
  },
  show_success(title) {
    Swal.fire({
      icon: "success",
      title,
      showConfirmButton: false,
      timer: 1200,
      position: "bottom-end",
      toast: true,
    });
  },
  show_error(message) {
    Swal.fire({
      icon: "error",
      title: message,
      showConfirmButton: false,
      timer: 3000,
      position: "bottom-end",
      toast: true,
    });
  },
};
